import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import NewsCard from './components/NewsCard';
import CategoryFilter from './components/CategoryFilter';
import TrendingTopics from './components/TrendingTopics';
import FeaturedCollection from './components/FeaturedCollection';
import SearchBar from './components/SearchBar';
import NewsletterSignup from './components/NewsletterSignup';
import { NewsService } from '../../services/newsService';
import { CollectionsService } from '../../services/collectionsService';

const NewsFeed = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data state
  const [newsData, setNewsData] = useState({
    articles: [],
    categories: [],
    trendingTopics: [],
    featuredCollection: null
  });

  // Load news data
  useEffect(() => {
    loadNewsData();
  }, []);

  // Filter articles when category or search changes
  useEffect(() => {
    filterArticles();
  }, [selectedCategory, searchQuery]);

  const loadNewsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        articlesResult,
        categoriesResult,
        trendingResult,
        collectionsResult
      ] = await Promise.all([
        NewsService?.getNewsArticles({ limit: 50 }),
        NewsService?.getNewsCategories(),
        NewsService?.getTrendingTopics(5),
        CollectionsService?.getPublicCollections(1) // Get one featured collection
      ]);

      // Handle results
      const articles = articlesResult?.data || [];
      const categories = categoriesResult?.data || [];
      const trending = trendingResult?.data || [];
      const collections = collectionsResult?.data || [];

      setNewsData({
        articles,
        categories,
        trendingTopics: trending,
        featuredCollection: collections?.[0] || null
      });

    } catch (err) {
      setError(err?.message || 'Failed to load news data');
    } finally {
      setLoading(false);
    }
  };

  const filterArticles = async () => {
    try {
      const options = {
        category: selectedCategory === 'All' ? null : selectedCategory?.toLowerCase()?.replace(' ', '-'),
        searchQuery: searchQuery || null,
        limit: 50
      };

      const { data: filteredArticles } = await NewsService?.getNewsArticles(options);
      
      setNewsData(prev => ({
        ...prev,
        articles: filteredArticles || []
      }));

    } catch (err) {
      console.error('Error filtering articles:', err);
    }
  };

  const handleBookmark = (articleId, isBookmarked) => {
    console.log(`Article ${articleId} ${isBookmarked ? 'bookmarked' : 'unbookmarked'}`);
    // Implement bookmark functionality
  };

  const handleShare = (article) => {
    if (navigator.share) {
      navigator.share({
        title: article?.title,
        text: article?.excerpt,
        url: `${window.location?.origin}/news/${article?.id}`,
      });
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard?.writeText(`${article?.title} - ${window.location?.origin}/news/${article?.id}`);
    }
  };

  const handleComment = (articleId) => {
    // Navigate to article detail page or open comment modal
    console.log('Opening comments for article:', articleId);
  };

  const handleTopicClick = (topic) => {
    setSearchQuery(topic?.title);
  };

  const handleViewCollection = (collection) => {
    console.log('Viewing collection:', collection?.name);
    // Navigate to collection view
  };

  const handleNewsletterSubscribe = (email) => {
    console.log('Newsletter subscription:', email);
    // Implement newsletter subscription
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>News & Insights | Crazy Rare Club</title>
          <meta name="description" content="Stay informed with the latest trends, market analysis, and community highlights in the rare collectibles world. Expert insights from Crazy Rare Club." />
          <meta name="keywords" content="collectibles news, rare collectibles market, collectibles trends, trading cards news, sports memorabilia, collectibles community" />
          <link rel="canonical" href="https://crazyrareclub.com/news-feed" />

          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Crazy Rare Club" />
          <meta property="og:title" content="News & Insights | Crazy Rare Club" />
          <meta property="og:description" content="Stay informed with the latest trends, market analysis, and community highlights in the rare collectibles world." />
          <meta property="og:url" content="https://crazyrareclub.com/news-feed" />
          <meta property="og:image" content="https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png" />
          <meta property="og:image:alt" content="Crazy Rare Club News & Insights — Collectibles market trends and analysis" />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="News & Insights | Crazy Rare Club" />
          <meta name="twitter:description" content="Stay informed with the latest trends, market analysis, and community highlights in the rare collectibles world." />
          <meta name="twitter:image" content="https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png" />

          {/* JSON-LD Structured Data */}
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Crazy Rare Club News & Insights",
            "description": "The latest trends, market analysis, and community highlights in the rare collectibles world.",
            "url": "https://crazyrareclub.com/news-feed",
            "image": "https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png",
            "publisher": {
              "@type": "Organization",
              "name": "Crazy Rare Club",
              "url": "https://crazyrareclub.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png"
              }
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://crazyrareclub.com" },
                { "@type": "ListItem", "position": 2, "name": "News & Insights", "item": "https://crazyrareclub.com/news-feed" }
              ]
            }
          })}</script>
        </Helmet>
        <Header />
        <Sidebar isCollapsed={isSidebarCollapsed} onToggle={setIsSidebarCollapsed} />
        <main className={`transition-all duration-300 pt-16 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        }`}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="text-muted-foreground">Loading news and insights...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>News & Insights | Crazy Rare Club</title>
          <meta name="description" content="Stay informed with the latest trends, market analysis, and community highlights in the rare collectibles world. Expert insights from Crazy Rare Club." />
          <meta name="keywords" content="collectibles news, rare collectibles market, collectibles trends, trading cards news, sports memorabilia, collectibles community" />
          <link rel="canonical" href="https://crazyrareclub.com/news-feed" />

          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Crazy Rare Club" />
          <meta property="og:title" content="News & Insights | Crazy Rare Club" />
          <meta property="og:description" content="Stay informed with the latest trends, market analysis, and community highlights in the rare collectibles world." />
          <meta property="og:url" content="https://crazyrareclub.com/news-feed" />
          <meta property="og:image" content="https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png" />
          <meta property="og:image:alt" content="Crazy Rare Club News & Insights — Collectibles market trends and analysis" />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="News & Insights | Crazy Rare Club" />
          <meta name="twitter:description" content="Stay informed with the latest trends, market analysis, and community highlights in the rare collectibles world." />
          <meta name="twitter:image" content="https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png" />

          {/* JSON-LD Structured Data */}
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Crazy Rare Club News & Insights",
            "description": "The latest trends, market analysis, and community highlights in the rare collectibles world.",
            "url": "https://crazyrareclub.com/news-feed",
            "image": "https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png",
            "publisher": {
              "@type": "Organization",
              "name": "Crazy Rare Club",
              "url": "https://crazyrareclub.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png"
              }
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://crazyrareclub.com" },
                { "@type": "ListItem", "position": 2, "name": "News & Insights", "item": "https://crazyrareclub.com/news-feed" }
              ]
            }
          })}</script>
        </Helmet>
        <Header />
        <Sidebar isCollapsed={isSidebarCollapsed} onToggle={setIsSidebarCollapsed} />
        <main className={`transition-all duration-300 pt-16 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        }`}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
            <div className="max-w-md mx-auto text-center">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertTriangle" size={32} className="text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Failed to Load News</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={loadNewsData}>Try Again</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const filteredArticles = newsData?.articles;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>News & Insights | Crazy Rare Club</title>
        <meta name="description" content="Stay informed with the latest trends, market analysis, and community highlights in the rare collectibles world. Expert insights from Crazy Rare Club." />
        <meta name="keywords" content="collectibles news, rare collectibles market, collectibles trends, trading cards news, sports memorabilia, collectibles community" />
        <link rel="canonical" href="https://crazyrareclub.com/news-feed" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Crazy Rare Club" />
        <meta property="og:title" content="News & Insights | Crazy Rare Club" />
        <meta property="og:description" content="Stay informed with the latest trends, market analysis, and community highlights in the rare collectibles world." />
        <meta property="og:url" content="https://crazyrareclub.com/news-feed" />
        <meta property="og:image" content="https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png" />
        <meta property="og:image:alt" content="Crazy Rare Club News & Insights — Collectibles market trends and analysis" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="News & Insights | Crazy Rare Club" />
        <meta name="twitter:description" content="Stay informed with the latest trends, market analysis, and community highlights in the rare collectibles world." />
        <meta name="twitter:image" content="https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Crazy Rare Club News & Insights",
          "description": "The latest trends, market analysis, and community highlights in the rare collectibles world.",
          "url": "https://crazyrareclub.com/news-feed",
          "image": "https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png",
          "publisher": {
            "@type": "Organization",
            "name": "Crazy Rare Club",
            "url": "https://crazyrareclub.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png"
            }
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://crazyrareclub.com" },
              { "@type": "ListItem", "position": 2, "name": "News & Insights", "item": "https://crazyrareclub.com/news-feed" }
            ]
          }
        })}</script>
      </Helmet>
      <Header />
      <Sidebar isCollapsed={isSidebarCollapsed} onToggle={setIsSidebarCollapsed} />
      <main className={`transition-all duration-300 pt-16 ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground font-accent mb-2">
                  News & Insights
                </h1>
                <p className="text-muted-foreground">
                  Stay informed with the latest trends, analysis, and community highlights in the collectibles world
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="hidden md:flex items-center space-x-2 bg-muted/30 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-colors duration-200 ${
                      viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="Grid3X3" size={16} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors duration-200 ${
                      viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="List" size={16} />
                  </button>
                </div>
                
                <Button
                  variant="outline"
                  iconName="Filter"
                  iconPosition="left"
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className="lg:hidden"
                >
                  Filters
                </Button>
              </div>
            </div>

            {/* Search Bar */}
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onSearch={(query) => setSearchQuery(query)}
              onFilterToggle={setIsFilterOpen}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Filters */}
            <div className={`lg:col-span-1 space-y-6 ${isFilterOpen ? 'block' : 'hidden lg:block'}`}>
              <CategoryFilter
                categories={newsData?.categories}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
              />
              
              <TrendingTopics
                topics={newsData?.trendingTopics}
                onTopicClick={handleTopicClick}
              />
              
              <NewsletterSignup
                onSubscribe={handleNewsletterSubscribe}
              />
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {/* Featured Collection */}
              {newsData?.featuredCollection && (
                <FeaturedCollection
                  collection={newsData?.featuredCollection}
                  onViewCollection={handleViewCollection}
                />
              )}

              {/* Results Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {selectedCategory === 'All' ? 'Latest News' : selectedCategory}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {filteredArticles?.length || 0} article{filteredArticles?.length !== 1 ? 's' : ''} found
                    {searchQuery && ` for "${searchQuery}"`}
                  </p>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">Sort by:</span>
                  <select className="bg-input border border-border rounded-lg px-3 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="latest">Latest</option>
                    <option value="trending">Trending</option>
                    <option value="popular">Most Popular</option>
                    <option value="bookmarked">Bookmarked</option>
                  </select>
                </div>
              </div>

              {/* Articles Grid */}
              {filteredArticles?.length > 0 ? (
                <div className={`grid gap-6 ${
                  viewMode === 'grid' ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1'
                }`}>
                  {filteredArticles?.map((article) => (
                    <NewsCard
                      key={article?.id}
                      article={article}
                      onBookmark={handleBookmark}
                      onShare={handleShare}
                      onComment={handleComment}
                      viewMode={viewMode}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Search" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    No articles found
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your search terms or category filters
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('All');
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              )}

              {/* Load More */}
              {filteredArticles?.length > 0 && (
                <div className="text-center pt-8">
                  <Button variant="outline" size="lg">
                    Load More Articles
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewsFeed;